import { baseAppApi } from '@/core/network/baseAppApi';
import { COMMON_HEADERS } from '@/core/network/network.constants';
import { getFirstErrorOrDefault } from '@/core/network/network.utils';
import { INVITATION_STATUS } from '@/core/utils/app.constants';
import { PORTAL_ROLES } from '@/authorization/authorization.constants';
import tokens from '@/users/tokens.json';
import userAgreementTokens from '@/user-agreement/tokens.json';
import { getBoardId } from '@/core/web-storage/webStorage.utils';

export type UserResponse = {
  id: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  email: string;
  portalRole: PORTAL_ROLES;
  invitationAccepted: boolean;
};

export type UserAgreementResponse = {
  isUserAgreementValid: boolean;
  userAgreementDate: string;
};

export type User = {
  id: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  email: string;
  portalRole: PORTAL_ROLES;
  invitationStatus: INVITATION_STATUS;
};

export type InviteUserRequestType = {
  user: InviteUserShape;
};

export type InviteUserShape = {
  firstName: string;
  lastName: string;
  jobTitle: string;
  email: string;
  portalRole: PORTAL_ROLES;
};

export type EditUserRequestType = {
  userEdits: EditUserShape;
  userId: string;
};

export type EditUserShape = {
  firstName: string | undefined;
  lastName: string | undefined;
  jobTitle: string | undefined;
  portalRole: PORTAL_ROLES | undefined;
};

const usersApi = baseAppApi.injectEndpoints({
  endpoints: (build) => ({
    getUsers: build.query<User[], void>({
      query() {
        const boardId = getBoardId();
        return {
          url: `v1/user-management/${boardId}/users`,
          method: 'GET',
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: { data: UserResponse[] }) {
        return response.data.map((userResponse) => {
          const user: User = {
            id: userResponse.id,
            firstName: userResponse.firstName,
            lastName: userResponse.lastName,
            jobTitle: userResponse.jobTitle,
            email: userResponse.email,
            portalRole: userResponse.portalRole,
            invitationStatus: userResponse.invitationAccepted
              ? INVITATION_STATUS.ACCEPTED
              : INVITATION_STATUS.PENDING,
          };
          return user;
        });
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['FETCHING_BANNER_ERROR']);
      },
      providesTags: ['USERS'],
    }),
    inviteUser: build.mutation<User, InviteUserRequestType>({
      query({ user }) {
        const boardId = getBoardId();
        return {
          url: `v1/user-management/${boardId}/users/invitation`,
          method: 'POST',
          body: {
            ...user,
          },
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: { data: UserResponse }) {
        const user: User = {
          id: response.data.id,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          jobTitle: response.data.jobTitle,
          email: response.data.email,
          portalRole: response.data.portalRole,
          invitationStatus: response.data.invitationAccepted
            ? INVITATION_STATUS.ACCEPTED
            : INVITATION_STATUS.PENDING,
        };
        return user;
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['INVITE_BANNER_ERROR']);
      },
      invalidatesTags: ['USERS'],
    }),
    resendInviteToUser: build.mutation<void, string>({
      query(userObjectId) {
        const boardId = getBoardId();
        return {
          url: `v1/user-management/${boardId}/users/${userObjectId}/invitation`,
          method: 'POST',
          headers: COMMON_HEADERS,
        };
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['RESEND_INVITE_BANNER_ERROR']);
      },
    }),
    deleteUser: build.mutation<string, string>({
      query(userObjectId) {
        const boardId = getBoardId();
        return {
          url: `v1/user-management/${boardId}/users/${userObjectId}`,
          method: 'DELETE',
          headers: COMMON_HEADERS,
        };
      },
      invalidatesTags: ['USERS'],
      transformResponse(_, meta) {
        let deletedUserId = '';
        const requestUrl = meta?.request.url;
        if (requestUrl) {
          const requestUrlTokens = requestUrl.split('/');
          deletedUserId = requestUrlTokens[requestUrlTokens.length - 1];
        }
        return deletedUserId;
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['DELETE_BANNER_ERROR']);
      },
    }),
    editUser: build.mutation<User, EditUserRequestType>({
      query({ userId, userEdits }) {
        const boardId = getBoardId();
        return {
          url: `v1/user-management/${boardId}/users/${userId}`,
          method: 'PATCH',
          body: {
            ...userEdits,
          },
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: { data: UserResponse }) {
        const user: User = {
          id: response.data.id,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          jobTitle: response.data.jobTitle,
          email: response.data.email,
          portalRole: response.data.portalRole,
          invitationStatus: response.data.invitationAccepted
            ? INVITATION_STATUS.ACCEPTED
            : INVITATION_STATUS.PENDING,
        };
        return user;
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['CHANGE_BANNER_ERROR']);
      },
      invalidatesTags: ['USERS'],
    }),
    getUserAgreementState: build.query<UserAgreementResponse, string>({
      query(userId) {
        const boardId = getBoardId();
        return {
          url: `v1/user-management/${boardId}/users/${userId}/user-agreement`,
          method: 'GET',
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: { data: UserAgreementResponse }) {
        return response.data;
      },
      transformErrorResponse(errorResponse) {
        return getFirstErrorOrDefault(errorResponse, userAgreementTokens['USER_AGREEMENT_ERROR']);
      },
    }),
    updateUserAgreementState: build.mutation<UserAgreementResponse, string>({
      query(userId) {
        const boardId = getBoardId();
        return {
          url: `v1/user-management/${boardId}/users/${userId}/user-agreement`,
          method: 'PATCH',
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: { data: UserAgreementResponse }) {
        return response.data;
      },
      transformErrorResponse(errorResponse) {
        return getFirstErrorOrDefault(errorResponse, userAgreementTokens['USER_AGREEMENT_ERROR']);
      },
    }),
    createDbUserFromAdUser: build.mutation<UserAgreementResponse, string>({
      query(userId) {
        const boardId = getBoardId();
        return {
          url: `v1/user-management/${boardId}/users/${userId}`,
          method: 'POST',
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(_) {
        return {
          isUserAgreementValid: false,
        } as UserAgreementResponse;
      },
      transformErrorResponse(errorResponse) {
        return getFirstErrorOrDefault(errorResponse, userAgreementTokens['USER_AGREEMENT_ERROR']);
      },
    }),
    getUserLastLogin: build.query<string, void>({
      query() {
        return {
          url: `v1/user-management/users/last-login`,
          method: 'GET',
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: { data: string }) {
        return response.data;
      },
      transformErrorResponse(errorResponse) {
        return getFirstErrorOrDefault(errorResponse, userAgreementTokens['USER_LAST_LOGIN_ERROR']);
      },
    }),
    updateUserLogin: build.mutation<void, void>({
      query() {
        return {
          url: `v1/user-management/users/login`,
          method: 'PATCH',
          headers: COMMON_HEADERS,
        };
      },
      transformErrorResponse(errorResponse) {
        return getFirstErrorOrDefault(errorResponse, userAgreementTokens['USER_LOGIN_ERROR']);
      },
    }),
    revokeUserSession: build.mutation<boolean, void>({
      query() {
        return {
          url: `v1/user-management/users/logout`,
          method: 'POST',
          headers: COMMON_HEADERS,
        };
      },
    }),
  }),
  overrideExisting: false,
});

export { usersApi };
